import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  OrganizationContextItem,
  PlatformAdminPortalService,
  PlatformAdminSession,
} from './platform-admin-portal.service';

@Injectable({ providedIn: 'root' })
export class PlatformAdminWorkspaceService {
  private readonly api = inject(PlatformAdminPortalService);
  private session: PlatformAdminSession | null = null;
  private readonly profileName = signal<string | null>(null);
  private organizations: OrganizationContextItem[] = [];
  private organizationContextPromise: Promise<OrganizationContextItem[]> | null = null;

  async resolveSession(): Promise<PlatformAdminSession | null> {
    this.profileName.set(null);
    this.session = null;

    const session = await firstValueFrom(this.api.verifySession());
    if (!session?.authenticated || !this.isPlatformAdmin(session)) {
      this.session = null;
      return null;
    }

    this.session = session;
    void this.loadOrganizationContext();
    return session;
  }

  currentSession(): PlatformAdminSession | null {
    return this.session;
  }

  acceptVerifiedSession(session: PlatformAdminSession | null | undefined): PlatformAdminSession | null {
    this.profileName.set(null);
    if (!session?.authenticated || !this.isPlatformAdmin(session)) {
      this.session = null;
      return null;
    }

    this.session = session;
    void this.loadOrganizationContext();
    return session;
  }

  clearSession(): void {
    this.profileName.set(null);
    this.session = null;
    this.organizations = [];
    this.organizationContextPromise = null;
  }

  visibleOrganizations(): OrganizationContextItem[] {
    return this.organizations;
  }

  async resolveOrganizations(): Promise<OrganizationContextItem[]> {
    if (!this.session?.authenticated) {
      const session = await this.resolveSession();
      if (!session) {
        return [];
      }
    }

    if (this.organizations.length) {
      return this.organizations;
    }

    return this.loadOrganizationContext();
  }

  isPlatformAdmin(session: PlatformAdminSession | null | undefined): boolean {
    return this.hasPlatformAdminRole(this.sessionRoles(session));
  }

  hasPlatformAdminRole(roles: unknown): boolean {
    const normalizedRoles = new Set(this.normalizedRoleList(roles));

    return normalizedRoles.has('ROLE_SUPER_ADMIN')
      || normalizedRoles.has('SUPER_ADMIN')
      || normalizedRoles.has('ROLE_ADMIN')
      || normalizedRoles.has('ADMIN');
  }

  normalizedRolesFor(sessionOrRoles: PlatformAdminSession | unknown): string[] {
    const maybeSession = sessionOrRoles as PlatformAdminSession | null | undefined;
    const rawRoles = maybeSession && typeof maybeSession === 'object' && (
      'roles' in maybeSession ||
      'authorities' in (maybeSession as any) ||
      'role' in (maybeSession as any) ||
      'authority' in (maybeSession as any) ||
      'user' in (maybeSession as any)
    )
      ? this.sessionRoles(maybeSession)
      : sessionOrRoles;

    return this.normalizedRoleList(rawRoles);
  }

  private sessionRoles(session: PlatformAdminSession | null | undefined): unknown {
    const rawSession = session as any;
    return rawSession?.roles
      ?? rawSession?.authorities
      ?? rawSession?.role
      ?? rawSession?.authority
      ?? rawSession?.user?.roles
      ?? rawSession?.user?.authorities
      ?? rawSession?.user?.role
      ?? rawSession?.user?.authority
      ?? [];
  }

  private normalizeRoles(roles: unknown): string[] {
    if (Array.isArray(roles)) {
      return roles.flatMap((role) => this.normalizeRoles(role));
    }

    if (roles && typeof roles === 'object') {
      const roleLike = roles as { role?: unknown; authority?: unknown; name?: unknown };
      return this.normalizeRoles(roleLike.role ?? roleLike.authority ?? roleLike.name ?? '');
    }

    const value = String(roles ?? '').trim();
    return value ? value.split(/[\s,;|]+/).filter(Boolean) : [];
  }

  private normalizedRoleList(roles: unknown): string[] {
    return this.normalizeRoles(roles)
      .map((role) => role.trim().toUpperCase())
      .filter(Boolean);
  }

  canManagePolicy(): boolean {
    return this.normalizedRolesFor(this.session).some(role => role === 'ROLE_SUPER_ADMIN' || role === 'SUPER_ADMIN');
  }

  displayName(): string {
    const session = this.currentSession();
    return this.profileName() || session?.displayName?.trim()
      || [session?.firstName, session?.lastName].filter(Boolean).join(' ').trim()
      || session?.email?.trim()
      || 'Platform Admin';
  }

  /** Updates only display fields; profile edits must never change administrative roles. */
  updateOwnDisplayName(profile: { firstName?: string; lastName?: string; displayName?: string; email?: string }): void {
    this.profileName.set(profile.displayName?.trim()
      || [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim()
      || profile.email || null);
    if (this.session) this.session = { ...this.session, firstName: profile.firstName,
      lastName: profile.lastName, displayName: profile.displayName };
  }

  async signOut(): Promise<void> {
    await firstValueFrom(this.api.logout()).catch(() => undefined);
    this.clearSession();
    window.location.href = `${environment.platformAdminAppUrl}/login`;
  }

  private async loadOrganizationContext(): Promise<OrganizationContextItem[]> {
    if (this.organizationContextPromise) {
      return this.organizationContextPromise;
    }

    this.organizationContextPromise = firstValueFrom(this.api.getOrganizationContext())
      .then((context) => context.organizations ?? [])
      .catch(() => [])
      .then((organizations) => {
        this.organizations = organizations;
        this.organizationContextPromise = null;
        return organizations;
      });

    return this.organizationContextPromise;
  }
}
